import { useEffect, useMemo, useRef, useState, type CSSProperties } from 'react';
import {
  ArrowUpRight,
  Check,
  FlaskConical,
  Layers,
  MessageSquareText,
  Microscope,
  Ruler,
  ScanSearch,
  Search,
  ShieldCheck,
  Stethoscope,
  TrendingUp,
  Wand2,
  X,
  type LucideIcon,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { AiBadge } from './AiBadge';
import { Chip } from './Chip';
import { PRODUCTS, TASKS, catalogueSections, requestHref, type CatalogueProduct, type CatalogueTask } from './catalogue';
import { hint, hover, ink, line, muted, page, small, surface } from './tokens';

type Kind = 'all' | 'commercial' | 'open';
const KINDS: Array<[Kind, string]> = [
  ['all', 'All'],
  ['commercial', 'Commercial'],
  ['open', 'Open source'],
];

/** Each section's icon, beside its name down the side and on its heading. */
const ICONS: Record<string, LucideIcon> = {
  'quality-control': ShieldCheck,
  'biomarker-scoring': FlaskConical,
  'cancer-detection': ScanSearch,
  grading: Ruler,
  'cells-and-tissue': Microscope,
  prediction: TrendingUp,
  specialties: Stethoscope,
  assistants: MessageSquareText,
  'image-tools': Wand2,
};
const iconOf = (category: string) => ICONS[category] ?? Layers;

const anchor = (category: string) => `more-${category}`;
const soft = 'bg-[color:var(--tr-soft,var(--muted))]';
const selected = 'bg-[color:var(--tr-row,var(--accent))]';

const TASK_BY_ID = new Map(TASKS.map((t) => [t.id, t]));
const VENDORS = new Set(PRODUCTS.filter((p) => !p.openSource).map((p) => p.vendor)).size;
/** How far below the top of the screen a section's heading counts as being read: under the top bar, where a click scrolls it (scroll-mt-24). */
const READING_LINE = 140;
/** After a click, the chosen section stays lit until the page has been still this long. */
const SETTLE_MS = 150;

/** Whether a product shows under the search and the kind chosen. */
export function matches(p: CatalogueProduct, task: CatalogueTask | undefined, query: string, kind: Kind): boolean {
  if (kind === 'open' && !p.openSource) return false;
  if (kind === 'commercial' && p.openSource) return false;
  const q = query.trim().toLowerCase();
  if (!q) return true;
  return [p.name, p.vendor, task?.name ?? '', ...(p.appliesTo ?? task?.appliesTo ?? [])].some((s) => s.toLowerCase().includes(q));
}

/**
 * Under the workspace's own actions, a marketplace of the ones it could ask
 * for (catalogue.ts): a search, commercial or open source, our sections down
 * the side, and in each section its tasks with a card per product.
 */
export function MoreActions({ workspaceName }: { workspaceName: string | undefined }) {
  const [query, setQuery] = useState('');
  const [kind, setKind] = useState<Kind>('all');
  const sections = useMemo(() => catalogueSections(TASKS, PRODUCTS.filter((p) => matches(p, TASK_BY_ID.get(p.task), query, kind))), [query, kind]);
  const [active, setActive] = useState<string | undefined>(sections[0]?.category);
  const root = useRef<HTMLElement>(null);
  // While set, a click chose the lit section and the page is still gliding to it.
  const settling = useRef<number | undefined>(undefined);

  // The section being read, lit in the list down the side: the last whose
  // heading has passed under the top bar, or the last of all at the end of
  // what scrolls the page. Any scroll counts (capture), as the enterprise
  // shell scrolls its own box, but only one that holds this page settles
  // the end; once per frame.
  useEffect(() => {
    let frame = 0;
    const pick = (box: Element | null) => {
      let current = sections[0]?.category;
      for (const s of sections) {
        const el = document.getElementById(anchor(s.category));
        if (el && el.getBoundingClientRect().top <= READING_LINE) current = s.category;
      }
      if (box && box.scrollHeight > box.clientHeight && box.scrollTop + box.clientHeight >= box.scrollHeight - 2) current = sections[sections.length - 1]?.category;
      setActive(current);
    };
    const onScroll = (e: Event) => {
      if (settling.current !== undefined) {
        window.clearTimeout(settling.current);
        settling.current = window.setTimeout(() => (settling.current = undefined), SETTLE_MS);
        return;
      }
      const target = e.target instanceof Element ? e.target : null;
      const box = target && root.current && target.contains(root.current) ? target : document.scrollingElement;
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => pick(box));
    };
    pick(document.scrollingElement);
    window.addEventListener('scroll', onScroll, { passive: true, capture: true });
    return () => {
      cancelAnimationFrame(frame);
      window.removeEventListener('scroll', onScroll, { capture: true });
    };
  }, [sections]);
  useEffect(() => () => window.clearTimeout(settling.current), []);

  const go = (category: string) => {
    setActive(category);
    window.clearTimeout(settling.current);
    settling.current = window.setTimeout(() => (settling.current = undefined), SETTLE_MS);
    document.getElementById(anchor(category))?.scrollIntoView?.({ behavior: 'smooth', block: 'start' });
  };

  return (
    <section ref={root} aria-labelledby="more-actions" className="flex flex-col gap-8">
      <div className={cn('flex flex-col gap-5 rounded-2xl border px-6 py-7 sm:px-8', line, soft)}>
        <div className="flex flex-col gap-1.5">
          <h2 id="more-actions" className={cn('text-[22px] font-semibold leading-tight tracking-[-0.01em]', ink)}>
            More actions
          </h2>
          <p className={cn('max-w-3xl text-sm leading-relaxed', muted)}>
            For {workspaceName ?? 'this workspace'}, request any of the {PRODUCTS.length} products below, from {VENDORS} vendors and open source.
          </p>
        </div>
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
          <label className={cn('flex h-10 w-full min-w-0 items-center gap-2 rounded-lg border px-3 sm:max-w-md sm:flex-1', line, page)}>
            <Search className={cn('h-4 w-4 shrink-0', hint)} aria-hidden />
            <input
              type="search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search products, vendors, stains or tissues"
              aria-label="Search more actions"
              // The browser's own clear button hidden: the X beside it is ours.
              className={cn(
                'h-full min-w-0 flex-1 bg-transparent text-sm outline-none placeholder:text-[color:var(--tr-hint,var(--muted-foreground))] [&::-webkit-search-cancel-button]:appearance-none',
                ink,
              )}
            />
            {query ? (
              <button type="button" onClick={() => setQuery('')} aria-label="Clear the search" className={cn('rounded p-0.5', hint, 'hover:text-[color:var(--tr-ink,var(--foreground))]')}>
                <X className="h-4 w-4" />
              </button>
            ) : null}
          </label>
          <div role="group" aria-label="Show" className={cn('inline-flex h-10 shrink-0 items-center gap-0.5 self-start rounded-lg border p-1 sm:self-auto', line, page)}>
            {KINDS.map(([k, label]) => (
              <button
                key={k}
                type="button"
                aria-pressed={kind === k}
                onClick={() => setKind(k)}
                className={cn('h-full rounded-md px-3 text-[13px] font-medium transition-colors', kind === k ? cn(selected, ink) : cn(muted, hover))}
              >
                {label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {sections.length === 0 ? (
        <div className={cn('flex flex-col items-center gap-3 rounded-xl border border-dashed px-6 py-12 text-center', line)}>
          <p className={cn('text-sm', muted)}>No products match.</p>
          <Button type="button" variant="outline" size="sm" onClick={() => { setQuery(''); setKind('all'); }}>
            Show all
          </Button>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-8 lg:grid-cols-[232px_minmax(0,1fr)] lg:gap-12">
          <nav aria-label="Sections of more actions" className="min-w-0">
            <ul className="-mx-6 flex gap-1.5 overflow-x-auto px-6 pb-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden lg:sticky lg:top-[76px] lg:mx-0 lg:flex-col lg:gap-0.5 lg:overflow-visible lg:px-0 lg:pb-0">
              {sections.map((s) => {
                const on = active === s.category;
                const Icon = iconOf(s.category);
                return (
                  <li key={s.category} className="shrink-0">
                    <button
                      type="button"
                      onClick={() => go(s.category)}
                      aria-current={on ? 'true' : undefined}
                      className={cn(
                        'flex w-full items-center justify-between gap-3 whitespace-nowrap rounded-full border px-3 py-1.5 text-left text-[13.5px] transition-colors lg:rounded-lg lg:border-transparent lg:py-2',
                        line,
                        on ? cn(selected, ink, 'font-medium') : cn(muted, hover),
                      )}
                    >
                      <span className="flex items-center gap-2.5">
                        <Icon className={cn('hidden h-4 w-4 shrink-0 lg:block', on ? ink : hint)} aria-hidden />
                        {s.title}
                      </span>
                      <span className={cn('text-xs tabular-nums', hint)}>{s.count}</span>
                    </button>
                  </li>
                );
              })}
            </ul>
          </nav>

          <div className="flex min-w-0 flex-col gap-14">
            {sections.map((s) => {
              const Icon = iconOf(s.category);
              return (
                <section key={s.category} id={anchor(s.category)} aria-labelledby={`${anchor(s.category)}-title`} className="flex scroll-mt-24 flex-col gap-7">
                  <div className={cn('flex items-center justify-between gap-3 border-b pb-3', line)}>
                    <div className="flex items-center gap-3">
                      <span aria-hidden className={cn('grid h-8 w-8 place-items-center rounded-lg border', line, page)}>
                        <Icon className={cn('h-4 w-4', ink)} />
                      </span>
                      <h3 id={`${anchor(s.category)}-title`} className={cn('text-[17px] font-semibold leading-tight tracking-[-0.005em]', ink)}>
                        {s.title}
                      </h3>
                    </div>
                    <span className={cn(small, hint)}>{s.count === 1 ? '1 product' : `${s.count} products`}</span>
                  </div>
                  {s.tasks.map((t) => (
                    <div key={t.id} className="flex flex-col gap-3">
                      <div className="flex flex-col gap-0.5">
                        <h4 className={cn('text-[14px] font-semibold leading-snug', ink)}>{t.name}</h4>
                        <p className={cn('max-w-3xl', small, muted)}>{t.summary}</p>
                      </div>
                      <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
                        {t.products.map((p) => (
                          <ProductCard key={p.id} product={p} task={t} />
                        ))}
                      </div>
                    </div>
                  ))}
                </section>
              );
            })}
          </div>
        </div>
      )}
    </section>
  );
}

// Calm hues for the vendors' monograms: blue, teal, plum, ochre, rose, green, cyan, rust.
const HUES = [214, 172, 268, 36, 340, 140, 192, 14];

/** The same hue for the same name, every time. */
function hueOf(name: string): number {
  let h = 0;
  for (const c of name) h = (h * 31 + c.charCodeAt(0)) >>> 0;
  return HUES[h % HUES.length];
}

/** "Leica Biosystems" → "LB"; "University of Edinburgh" → "UE"; "Paige" → "Pa"; "Pathol.AI" → "PA". */
export function initialsOf(name: string): string {
  const words = name.split(/[\s.,\-_]+/).filter((w) => w && !/^(of|the|and|for|de)$/.test(w));
  if (words.length >= 2) return (words[0][0] + words[1][0]).toUpperCase();
  const w = words[0] ?? '';
  return w.slice(0, 1).toUpperCase() + w.slice(1, 2);
}

/** A vendor's mark, until we carry logos: its initials on its own soft colour. */
function Monogram({ name }: { name: string }) {
  return (
    <span
      aria-hidden
      style={{ '--mono': hueOf(name) } as CSSProperties}
      className={cn(
        'grid h-10 w-10 shrink-0 place-items-center rounded-[10px] text-[13px] font-semibold tracking-tight ring-1 ring-inset ring-black/5',
        'bg-[hsl(var(--mono)_42%_93%)] text-[hsl(var(--mono)_38%_30%)]',
        'dark:bg-[hsl(var(--mono)_22%_22%)] dark:text-[hsl(var(--mono)_45%_80%)] dark:ring-white/10',
      )}
    >
      {initialsOf(name)}
    </span>
  );
}

/** One product: who makes it, what it is made for, and Request. */
function ProductCard({ product: p, task }: { product: CatalogueProduct; task: CatalogueTask }) {
  const [requested, setRequested] = useState(false);
  const href = requestHref(p);
  const label = `${p.name}, ${p.vendor}`;
  return (
    <article
      aria-label={label}
      className={cn(
        'flex flex-col rounded-xl border transition-[box-shadow,transform,border-color] duration-150',
        'hover:-translate-y-px hover:border-[color:var(--tr-line-2,var(--border))] hover:shadow-[0_8px_24px_-12px_rgba(15,15,16,0.22)]',
        line,
        surface,
      )}
    >
      <div className="flex items-start gap-3 px-4 pt-4">
        <Monogram name={p.vendor} />
        <div className="flex min-w-0 flex-1 flex-col gap-0.5">
          <div className="flex items-start justify-between gap-2">
            <h5 className={cn('min-w-0 text-[14px] font-semibold leading-snug', ink)}>{p.name}</h5>
            {p.ai ? <AiBadge className="mt-px" /> : null}
          </div>
          <p className={cn('truncate', small, muted)}>{p.openSource ? `${p.vendor} · Open source` : p.vendor}</p>
        </div>
      </div>
      <div className="mt-auto flex items-end justify-between gap-3 px-4 pt-4 pb-4">
        {requested ? (
          <p role="status" className={cn('self-center text-xs', muted)}>
            Thanks, we’ll be in touch.
          </p>
        ) : (
          <div className="flex min-w-0 flex-wrap gap-1.5">
            {(p.appliesTo ?? task.appliesTo).map((t) => (
              <Chip key={t}>{t}</Chip>
            ))}
          </div>
        )}
        {href ? (
          <Button asChild variant="outline" size="sm" className="shrink-0">
            <a href={href} target="_blank" rel="noopener noreferrer" aria-label={`Request ${label}`}>
              Request
              <ArrowUpRight />
            </a>
          </Button>
        ) : (
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="shrink-0"
            disabled={requested}
            onClick={() => setRequested(true)}
            aria-label={`${requested ? 'Requested' : 'Request'} ${label}`}
          >
            {requested ? <Check /> : null}
            {requested ? 'Requested' : 'Request'}
          </Button>
        )}
      </div>
    </article>
  );
}
